"use client";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Download, FileArchive, Minimize2, Scissors } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import { formatFileSize, parsePageRanges } from "@/lib/tools/pdf";

type PdfOutput = {
  url: string;
  fileName: string;
  pageCount: number;
  byteSize: number;
  sourceByteSize?: number;
  note?: string;
};

function isPdfFile(file: File) {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

async function loadPdf(file: File) {
  if (!isPdfFile(file)) {
    throw new Error(`${file.name} is not a PDF file.`);
  }

  try {
    return await PDFDocument.load(await file.arrayBuffer(), {
      updateMetadata: false,
    });
  } catch {
    throw new Error(`${file.name} could not be read. Encrypted or damaged PDFs are not supported.`);
  }
}

function pdfBytesToObjectUrl(bytes: Uint8Array) {
  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(arrayBuffer).set(bytes);

  return URL.createObjectURL(
    new Blob([arrayBuffer], {
      type: "application/pdf",
    }),
  );
}

function usePdfOutput() {
  const [output, setOutput] = useState<PdfOutput | null>(null);
  const objectUrlRef = useRef("");

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );

  const clearOutput = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }

    setOutput(null);
  };

  const replaceOutput = (next: Omit<PdfOutput, "url"> & { bytes: Uint8Array }) => {
    clearOutput();
    const url = pdfBytesToObjectUrl(next.bytes);
    objectUrlRef.current = url;
    setOutput({
      url,
      fileName: next.fileName,
      pageCount: next.pageCount,
      byteSize: next.byteSize,
      sourceByteSize: next.sourceByteSize,
      note: next.note,
    });
  };

  return { output, clearOutput, replaceOutput };
}

function PdfDownloadButton({ output }: { output: PdfOutput | null }) {
  if (!output) {
    return null;
  }

  return (
    <a
      href={output.url}
      download={output.fileName}
      className={buttonVariants({ variant: "secondary", size: "sm" })}
    >
      <Download className="h-4 w-4" />
      Download
    </a>
  );
}

function PdfOutputPanel({ output, placeholder }: { output: PdfOutput | null; placeholder: string }) {
  if (!output) {
    return <CodeOutput placeholder={placeholder} />;
  }

  const delta =
    output.sourceByteSize && output.sourceByteSize > 0
      ? output.byteSize - output.sourceByteSize
      : null;
  const deltaLabel =
    delta === null
      ? null
      : `${delta <= 0 ? "-" : "+"}${formatFileSize(Math.abs(delta))}`;

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-left text-sm">
          <tbody>
            <tr className="border-b border-zinc-100">
              <th className="w-40 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">File</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-950">{output.fileName}</td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Pages</th>
              <td className="px-3 py-2 font-mono text-zinc-950">{output.pageCount}</td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Size</th>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {formatFileSize(output.byteSize)}
                {deltaLabel ? <span className="ml-2 text-zinc-500">({deltaLabel})</span> : null}
              </td>
            </tr>
            {output.note ? (
              <tr>
                <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Note</th>
                <td className="px-3 py-2 text-zinc-700">{output.note}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <iframe
        src={output.url}
        title="PDF preview"
        className="h-[34rem] w-full rounded-md border border-zinc-200 bg-zinc-50"
      />
    </div>
  );
}

export function PdfMergeTool() {
  const [files, setFiles] = useState<File[]>([]);
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { output, clearOutput, replaceOutput } = usePdfOutput();

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(event.target.files ?? []));
    clearOutput();
    setError("");
  };

  const clear = () => {
    setFiles([]);
    setInputKey((value) => value + 1);
    setError("");
    clearOutput();
  };

  const merge = async () => {
    if (files.length < 2) {
      setError("Choose at least two PDF files.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const mergedPdf = await PDFDocument.create({ updateMetadata: false });
      let pageCount = 0;
      let sourceByteSize = 0;

      for (const file of files) {
        const sourcePdf = await loadPdf(file);
        const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
        pageCount += sourcePdf.getPageCount();
        sourceByteSize += file.size;
      }

      const bytes = await mergedPdf.save({ useObjectStreams: true, addDefaultPage: false });
      replaceOutput({
        bytes,
        fileName: "merged.pdf",
        pageCount,
        byteSize: bytes.byteLength,
        sourceByteSize,
      });
    } catch (mergeError) {
      clearOutput();
      setError(mergeError instanceof Error ? mergeError.message : "PDFs could not be merged.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="PDF files"
      outputTitle="Merged PDF"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={<PdfDownloadButton output={output} />}
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pdf-merge-files">Files</Label>
            <Input
              key={inputKey}
              id="pdf-merge-files"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={onFileChange}
            />
          </div>
          {files.length ? (
            <div className="overflow-hidden rounded-md border border-zinc-200">
              <table className="w-full text-left text-sm">
                <tbody>
                  {files.map((file, index) => (
                    <tr key={`${file.name}-${file.lastModified}`} className="border-b border-zinc-100 last:border-0">
                      <th className="w-12 bg-zinc-50 px-3 py-2 font-mono text-zinc-500">
                        {index + 1}
                      </th>
                      <td className="break-all px-3 py-2 text-zinc-900">{file.name}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-zinc-500">
                        {formatFileSize(file.size)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <ErrorMessage message={error} />
          <Button type="button" onClick={merge} disabled={busy}>
            <FileArchive className="h-4 w-4" />
            {busy ? "Merging..." : "Merge PDFs"}
          </Button>
        </div>
      }
      output={<PdfOutputPanel output={output} placeholder="Merged PDF details will appear here." />}
    />
  );
}

export function PdfSplitTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [ranges, setRanges] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { output, clearOutput, replaceOutput } = usePdfOutput();

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setFile(nextFile);
    setPageCount(0);
    setRanges("");
    setError("");
    clearOutput();

    if (!nextFile) {
      return;
    }

    try {
      const pdf = await loadPdf(nextFile);
      const pages = pdf.getPageCount();
      setPageCount(pages);
      setRanges(pages > 1 ? `1-${pages}` : "1");
    } catch (fileError) {
      setFile(null);
      setError(fileError instanceof Error ? fileError.message : "PDF could not be read.");
    }
  };

  const clear = () => {
    setFile(null);
    setPageCount(0);
    setRanges("");
    setInputKey((value) => value + 1);
    setError("");
    clearOutput();
  };

  const split = async () => {
    if (!file) {
      setError("Choose a PDF file.");
      return;
    }

    const parsedRanges = parsePageRanges(ranges, pageCount);

    if (!parsedRanges.ok) {
      setError(parsedRanges.error);
      return;
    }

    setBusy(true);
    setError("");

    try {
      const sourcePdf = await loadPdf(file);
      const splitPdf = await PDFDocument.create({ updateMetadata: false });
      const pages = await splitPdf.copyPages(sourcePdf, parsedRanges.value);
      pages.forEach((page) => splitPdf.addPage(page));
      const bytes = await splitPdf.save({ useObjectStreams: true, addDefaultPage: false });

      replaceOutput({
        bytes,
        fileName: `${file.name.replace(/\.pdf$/i, "") || "document"}-split.pdf`,
        pageCount: pages.length,
        byteSize: bytes.byteLength,
        sourceByteSize: file.size,
      });
    } catch (splitError) {
      clearOutput();
      setError(splitError instanceof Error ? splitError.message : "PDF pages could not be extracted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Split settings"
      outputTitle="Extracted PDF"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={<PdfDownloadButton output={output} />}
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pdf-split-file">File</Label>
            <Input
              key={inputKey}
              id="pdf-split-file"
              type="file"
              accept="application/pdf,.pdf"
              onChange={onFileChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pdf-split-ranges">Page ranges</Label>
            <Textarea
              id="pdf-split-ranges"
              value={ranges}
              onChange={(event) => setRanges(event.target.value)}
              placeholder={pageCount ? `1-${pageCount}` : "1-3, 5, 8-10"}
              className="min-h-24"
            />
            {pageCount ? (
              <p className="text-sm text-zinc-500">{pageCount} pages detected.</p>
            ) : null}
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={split} disabled={busy}>
            <Scissors className="h-4 w-4" />
            {busy ? "Splitting..." : "Extract pages"}
          </Button>
        </div>
      }
      output={<PdfOutputPanel output={output} placeholder="Extracted PDF details will appear here." />}
    />
  );
}

export function PdfCompressTool() {
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { output, clearOutput, replaceOutput } = usePdfOutput();

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setError("");
    clearOutput();
  };

  const clear = () => {
    setFile(null);
    setInputKey((value) => value + 1);
    setError("");
    clearOutput();
  };

  const optimize = async () => {
    if (!file) {
      setError("Choose a PDF file.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      const sourcePdf = await loadPdf(file);
      const optimizedPdf = await PDFDocument.create({ updateMetadata: false });
      const pages = await optimizedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
      pages.forEach((page) => optimizedPdf.addPage(page));
      const bytes = await optimizedPdf.save({ useObjectStreams: true, addDefaultPage: false });

      replaceOutput({
        bytes,
        fileName: `${file.name.replace(/\.pdf$/i, "") || "document"}-optimized.pdf`,
        pageCount: pages.length,
        byteSize: bytes.byteLength,
        sourceByteSize: file.size,
        note: "Lossless page rewrite. Embedded images are preserved, not recompressed.",
      });
    } catch (optimizeError) {
      clearOutput();
      setError(optimizeError instanceof Error ? optimizeError.message : "PDF could not be optimized.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="PDF file"
      outputTitle="Optimized PDF"
      inputActions={<ClearButton onClear={clear} />}
      outputActions={<PdfDownloadButton output={output} />}
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pdf-compress-file">File</Label>
            <Input
              key={inputKey}
              id="pdf-compress-file"
              type="file"
              accept="application/pdf,.pdf"
              onChange={onFileChange}
            />
            {file ? <p className="text-sm text-zinc-500">{formatFileSize(file.size)}</p> : null}
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={optimize} disabled={busy}>
            <Minimize2 className="h-4 w-4" />
            {busy ? "Optimizing..." : "Optimize PDF"}
          </Button>
        </div>
      }
      output={<PdfOutputPanel output={output} placeholder="Optimized PDF details will appear here." />}
    />
  );
}
